import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { isStaleChunkError, rememberReturnTo } from '@iqd/shared-app';
import { App } from './app/app';
import { Auth0Bridge } from './app/providers/auth0-bridge';
import { ApolloProvider } from './app/providers/apollo-provider';
import { normaliseLegacyPath } from './app/shared/host-path';
import { ThemeProvider } from './app/shared/ui';
import { environment } from './environments/environment';
import './styles.css';

/**
 * Old quote links, in the shape this app used to write them.
 *
 * The quote, its revision and its option were query parameters on a static
 * path; they are path segments now. People have these bookmarked, so every
 * incoming URL is converted here, before the router reads it.
 */
function normaliseUrl() {
  const current = `${window.location.pathname}${window.location.search}`;
  const normalised = normaliseLegacyPath(current);
  if (normalised !== current) {
    window.history.replaceState(null, '', `${normalised}${window.location.hash}`);
  }
}

normaliseUrl();

if (environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.version,
    // Same as iqd-pro: a tab open across a deploy fails to fetch a chunk that
    // has been deleted from the bucket, `NewBuildNotice` already says to
    // reload, and reporting it files an issue per stale tab about a build that
    // is working.
    beforeSend(event, hint) {
      const thrown = hint.originalException ?? event.exception?.values?.[0]?.value;
      return isStaleChunkError(thrown) ? null : event;
    },
  });
}

// A data router, not <BrowserRouter>: useUnsavedChangesGuard blocks navigation
// away from the estimator with useBlocker, and that needs the data-router
// context. The whole app hangs off one splat route — the routes themselves stay
// declarative inside <App />, which the data APIs are not used for.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <Auth0Bridge>
        <ApolloProvider>
          <App />
        </ApolloProvider>
      </Auth0Bridge>
    ),
  },
]);

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    {/* Outside the router: the theme is chrome, not a route, and the attribute
        it writes is on the document rather than on anything React renders. */}
    <ThemeProvider>
      <Auth0Provider
        domain={environment.auth0.domain}
        clientId={environment.auth0.clientId}
        // Auth0 recommends renewing through a first-party silent-auth iframe over
        // holding a refresh token, when the Auth0 domain is same-site with the
        // app: nothing renewable is left at rest for an XSS to read. Where that
        // does not hold — localhost in development — fall back to refresh tokens.
        useRefreshTokens={!environment.auth0.useSilentAuth}
        // No iframe behind the refresh token. The fallback exists for an app
        // that can renew both ways, and this one cannot: refresh tokens are
        // only used where the iframe would be third-party — localhost — so
        // falling back to it buys a timeout and then the same failure. Failing
        // at once is what puts the session dialog up while it still reads as a
        // consequence of what someone just did. Matches iqd-pro.
        useRefreshTokensFallback={false}
        cacheLocation={environment.auth0.useSilentAuth ? 'memory' : 'localstorage'}
        // Where a sign-in came from. Callers put it in `appState.returnTo`,
        // and without this nothing reads it — every sign-in landed on the
        // dashboard, including one started because the session expired
        // mid-screen, which then quietly moved you somewhere else.
        //
        // Remembered as well as navigated to: this fires before the SDK
        // reports itself loaded, so `CallbackPage` can still render after
        // this navigation has moved the URL, and has to send people to the
        // same place rather than to the dashboard.
        onRedirectCallback={(appState) => {
          const target = appState?.returnTo ?? '/';
          rememberReturnTo(target);
          void router.navigate(target, { replace: true });
        }}
        authorizationParams={{
          audience: environment.auth0.audience,
          redirect_uri: `${window.location.origin}/callback`,
          scope: 'openid profile email',
        }}
      >
        <RouterProvider router={router} />
      </Auth0Provider>
    </ThemeProvider>
  </StrictMode>,
);
